import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './PlaySheet.css';

interface Props {
  ariaLabel: string;
  onClose: () => void;
  children: React.ReactNode;
  testId?: string;
}

/**
 * Play's bottom sheet (Elite sheet review, item 2): pinned to the bottom,
 * sized by its content with no minimum height, #101013 on a #2b2b32 top
 * border with a 24px top radius, 0 20px 34px padding and a 14px gap between
 * children. The 28px grab handle row sits on top; the rgba(0,0,0,.62) backdrop
 * and Escape close it. Portalled to body and locks page scroll like the
 * shared MobileSheet, which is fixed-height and so does not fit here.
 */
export const PlaySheet: React.FC<Props> = ({ ariaLabel, onClose, children, testId }) => {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <>
      <div className="play-sheet__backdrop" onClick={onClose} aria-hidden="true" data-testid={testId ? `${testId}-backdrop` : undefined} />
      <div className="play-sheet" role="dialog" aria-modal="true" aria-label={ariaLabel} data-testid={testId}>
        <button type="button" className="play-sheet__handle" onClick={onClose} aria-label={`Close ${ariaLabel.toLowerCase()}`}>
          <span className="play-sheet__handle-bar" />
        </button>
        {children}
      </div>
    </>,
    document.body,
  );
};

export default PlaySheet;
